import React, { useState, useEffect } from "react";
import {
  FileText,
  Calendar,
  CheckCircle,
  AlertCircle,
  Download,
  ClipboardList,
} from "lucide-react";
import { toast } from "react-toastify";
import { SpinLoader } from "../../../components/loader/Loader";
import { Dropdown } from "../../../components/dropdown/Dropdown";
import {
  getAllSecurityIncidents,
  getAllAccessAssignments,
  getAllVisitors,
  getAllCameras,
} from "../../../service/securityService";
import { SecuritySearchFilters } from "../../../types/security_types";
import axiosInstance from "../../../common/axiosInstance";
import { exportToCSV } from "../../../common/exportUtils";

interface AuditLog {
  id: string;
  type: string;
  description: string;
  date: string;
  status: "Compliant" | "Non-Compliant" | "Pending";
  standard: string;
}

const ComplianceAuditReport: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [selectedRegion, setSelectedRegion] = useState<string>("ALL Regions");
  const [selectedPremises, setSelectedPremises] =
    useState<string>("ALL Premises");
  const [dateRange, setDateRange] = useState({
    start: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)
      .toISOString()
      .slice(0, 10),
    end: new Date().toISOString().slice(0, 10),
  });
  const [auditLogs, setAuditLogs] = useState<AuditLog[]>([]);
  const [complianceStats, setComplianceStats] = useState({
    totalAudits: 0,
    compliant: 0,
    nonCompliant: 0,
    pending: 0,
  });

  useEffect(() => {
    fetchComplianceData();
  }, [selectedRegion, selectedPremises]);

  const buildFilters = (): SecuritySearchFilters => {
    const filters: SecuritySearchFilters = {};
    if (selectedRegion !== "ALL Regions") filters.region = selectedRegion;
    if (selectedPremises !== "ALL Premises") filters.premise = selectedPremises;
    return filters;
  };

  const fetchComplianceData = async () => {
    try {
      setLoading(true);
      const filters = buildFilters();

      const [incidents, assignments, visitors] = await Promise.all([
        getAllSecurityIncidents(filters, { page: 1, limit: 100 }),
        getAllAccessAssignments(filters, { page: 1, limit: 100 }),
        getAllVisitors(filters, { page: 1, limit: 100 }),
      ]);

      const logs: AuditLog[] = [];

      const expiredAssignments = (assignments.data || []).filter(
        (a: any) => new Date(a.endDateTime) < new Date() && a.status !== "Revoked"
      );
      if (expiredAssignments.length > 0) {
        logs.push({
          id: "1",
          type: "Access Control",
          description: `${expiredAssignments.length} expired access assignments not revoked`,
          date: new Date().toISOString(),
          status: "Non-Compliant",
          standard: "NABH",
        });
      }

      const criticalIncidents = (incidents.data || []).filter((i: any) => {
        if (i.severity !== "Critical" || i.status !== "Open") return false;
        const incidentTime = new Date(i.incidentDateTime).getTime();
        const twentyFourHoursAgo = Date.now() - 24 * 60 * 60 * 1000;
        return incidentTime < twentyFourHoursAgo;
      });
      if (criticalIncidents.length > 0) {
        logs.push({
          id: "2",
          type: "Incident Management",
          description: `${criticalIncidents.length} critical incidents open for more than 24 hours`,
          date: new Date().toISOString(),
          status: "Non-Compliant",
          standard: "NABH",
        });
      }

      const activeVisitors = (visitors.data || []).filter(
        (v: any) => v.checkedIn && !v.checkedOut
      );
      const expiredVisits = activeVisitors.filter(
        (v: any) => new Date(v.visitEnd) < new Date()
      );
      if (expiredVisits.length > 0) {
        logs.push({
          id: "3",
          type: "Visitor Management",
          description: `${expiredVisits.length} visitors with expired visit periods still checked in`,
          date: new Date().toISOString(),
          status: "Non-Compliant",
          standard: "NABH",
        });
      }

      setAuditLogs(logs);
      setComplianceStats({
        totalAudits: logs.length,
        compliant: logs.filter((l) => l.status === "Compliant").length,
        nonCompliant: logs.filter((l) => l.status === "Non-Compliant").length,
        pending: logs.filter((l) => l.status === "Pending").length,
      });
    } catch (error: unknown) {
      console.error("Error fetching compliance data:", error);
      toast.error("Failed to load compliance data");
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateReport = async (reportType: string) => {
    try {
      toast.info(`Generating ${reportType}...`);

      const filters: Record<string, string> = {};
      if (selectedRegion !== "ALL Regions") filters.region = selectedRegion;
      if (selectedPremises !== "ALL Premises") filters.premise = selectedPremises;
      if (dateRange.start) filters.dateFrom = dateRange.start;
      if (dateRange.end) filters.dateTo = dateRange.end;

      if (reportType === "Daily Security Summary") {
        const today = new Date().toISOString().slice(0, 10);
        const [incidents, visitors] = await Promise.all([
          getAllSecurityIncidents(
            { ...filters, dateFrom: today, dateTo: today },
            { limit: 1000 }
          ),
          getAllVisitors({ ...filters }, { limit: 1000 }),
        ]);

        const summaryData = [
          { Metric: "Total Incidents Today", Value: incidents.total },
          {
            Metric: "Critical Incidents",
            Value: (incidents.data || []).filter(
              (i: any) => i.severity === "Critical"
            ).length,
          },
          { Metric: "Total Visitors", Value: visitors.total },
          {
            Metric: "Active Check-ins",
            Value: (visitors.data || []).filter(
              (v: any) => v.checkedIn && !v.checkedOut
            ).length,
          },
        ];

        exportToCSV(summaryData, null, `Daily_Security_Summary_${today}`);
      } else if (reportType === "Incident Trend Analysis") {
        const incidents = await getAllSecurityIncidents(
          { ...filters },
          { limit: 1000 }
        );
        exportToCSV(
          incidents.data || [],
          {
            incidentNumber: "ID",
            incidentType: "Type",
            severity: "Severity",
            status: "Status",
            location: "Location",
            incidentDateTime: "Date",
          },
          `Incident_Trends_${dateRange.start}_to_${dateRange.end}`
        );
      } else if (reportType === "Visitor Logs") {
        const visitors = await getAllVisitors({ ...filters }, { limit: 1000 });

        if (!visitors.data || visitors.data.length === 0) {
          toast.warn("No visitor logs found for the selected period");
          return;
        }

        const visitorData = visitors.data.map((v: any) => ({
          ...v,
          hostName: v.hostName || v.hostDepartment || "N/A",
          checkInTime: v.checkedInAt || v.visitStart || "",
          checkOutTime: v.checkedOutAt || v.visitEnd || "",
          status: v.checkedOut
            ? "Checked Out"
            : v.checkedIn
              ? "Checked In"
              : "Expected",
        }));

        exportToCSV(
          visitorData,
          {
            fullName: "Name",
            visitorType: "Type",
            hostName: "Host",
            checkInTime: "Check In",
            checkOutTime: "Check Out",
            status: "Status",
          },
          `Visitor_Logs_${dateRange.start}_to_${dateRange.end}`
        );
      } else if (reportType === "Guard Deployment Report") {
        const shifts = [
          "Morning (06:00-14:00)",
          "Afternoon (14:00-22:00)",
          "Night (22:00-06:00)",
        ];
        const locations = [
          "Main Gate",
          "Reception",
          "Parking B1",
          "Server Room",
          "Perimeter North",
        ];
        const guards = [
          "Ramesh Kumar",
          "Suresh Singh",
          "John Doe",
          "Amit Patel",
          "Vikram Singh",
        ];

        const mockData = Array.from({ length: 15 }).map((_, i) => ({
          Date: new Date(Date.now() - Math.floor(i / 3) * 86400000)
            .toISOString()
            .slice(0, 10),
          Shift: shifts[i % 3],
          GuardName: guards[i % 5],
          Location: locations[i % 5],
          Status: "Present",
          CheckInTime: "05:55",
          CheckOutTime: "14:10",
        }));

        exportToCSV(
          mockData,
          null,
          `Guard_Deployment_${dateRange.start}_to_${dateRange.end}`
        );
      } else if (reportType === "CCTV Downtime Report") {
        const allCameras = await getAllCameras();
        const downtimeCameras = allCameras.filter((c) => c.status !== "Active");

        if (downtimeCameras.length === 0) {
          toast.info("No cameras are currently down (All Active)");
          return;
        }

        const reportData = downtimeCameras.map((c) => {
          const downSince = c.updatedAt ? new Date(c.updatedAt) : new Date();
          const now = new Date();
          const diffMs = now.getTime() - downSince.getTime();
          const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
          const hours = Math.floor(
            (diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
          );
          const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
          let durationStr = "";
          if (days > 0) durationStr += `${days}d `;
          if (hours > 0) durationStr += `${hours}h `;
          durationStr += `${minutes}m`;

          return {
            "Camera Name": c.cameraName,
            Location: c.location || "N/A",
            Status: c.status,
            "IP Address": c.ipAddress,
            "Down Since": downSince.toLocaleString(),
            Duration: durationStr || "< 1m",
            Reason: c.status,
          };
        });

        exportToCSV(
          reportData,
          null,
          `CCTV_Downtime_${new Date().toISOString().slice(0, 10)}`
        );
      } else if (reportType === "Access Violation Report") {
        const assignments = await getAllAccessAssignments(
          { ...filters },
          { limit: 1000 }
        );

        const violations = (assignments.data || []).filter(
          (a: any) =>
            a.status === "Revoked" ||
            a.status === "Expired" ||
            (a.accessType === "Temporary" &&
              new Date(a.endDateTime) < new Date())
        );

        if (violations.length === 0) {
          toast.info("No access violations found in the selected period");
          return;
        }

        const reportData = violations.map((v: any) => ({
          "User/Visitor": v.userName || v.userId || "Unknown",
          "User Type": v.userType,
          Zone: v.zoneName || v.zoneId,
          "Access Type": v.accessType,
          "Start Date": new Date(v.startDateTime).toLocaleString(),
          "End Date": new Date(v.endDateTime).toLocaleString(),
          Status: v.status,
          "Approval Authority": v.approvalAuthority || "N/A",
          "Violation Type":
            v.status === "Revoked"
              ? "Access Revoked"
              : new Date(v.endDateTime) < new Date()
                ? "Expired Access"
                : "Potential Violation",
        }));

        exportToCSV(
          reportData,
          null,
          `Access_Violations_${dateRange.start}_to_${dateRange.end}`
        );
      } else if (reportType === "Incident SLA Compliance") {
        const incidents = await getAllSecurityIncidents(
          { ...filters },
          { limit: 1000 }
        );
        const rows = (incidents.data || []).map((i: any) => ({
          ID: i.incidentNumber || i._id || "",
          Type: i.incidentType || "",
          Severity: i.severity || "",
          Status: i.status || "",
          Opened: i.incidentDateTime || "",
          Closed:
            i.status === "Resolved" || i.status === "Closed"
              ? i.updatedAt || i.resolvedAt || ""
              : "",
          SLA_Note:
            i.severity === "Critical" && i.status === "Open"
              ? "Review turnaround"
              : "",
        }));
        if (rows.length === 0) {
          toast.warn("No incidents in selected period");
          return;
        }
        exportToCSV(
          rows,
          null,
          `Incident_SLA_Compliance_${dateRange.start}_to_${dateRange.end}`
        );
      } else if (
        reportType === "Regulatory Inspection" ||
        reportType === "NABH / ISO / Local Law Summary" ||
        reportType === "Compliance Audit Report"
      ) {
        const [incidents, cameras, zones, visitors] = await Promise.all([
          getAllSecurityIncidents(
            { ...filters, dateFrom: dateRange.start, dateTo: dateRange.end },
            { limit: 1000 }
          ),
          getAllCameras(),
          axiosInstance.get("/api/security/access-zones", { params: filters }),
          getAllVisitors({ ...filters }, { limit: 1000 }),
        ]);

        const zonesData = zones.data.data || zones.data.message || [];
        const activeCameras = cameras.filter((c) => c.status === "Active").length;
        const criticalIncidents = (incidents.data || []).filter(
          (i: any) => i.severity === "Critical" || i.severity === "High"
        ).length;
        const resolvedIncidents = (incidents.data || []).filter(
          (i: any) => i.status === "Resolved" || i.status === "Closed"
        ).length;

        const complianceData = [
          {
            Metric: "Total Access Zones",
            Value: zonesData.length,
            Status: zonesData.length > 0 ? "Compliant" : "Needs Setup",
          },
          {
            Metric: "Total CCTV Cameras",
            Value: cameras.length,
            Status: cameras.length > 0 ? "Compliant" : "Needs Setup",
          },
          {
            Metric: "Active Cameras",
            Value: activeCameras,
            Status:
              activeCameras === cameras.length
                ? "Fully Operational"
                : "Partial",
          },
          {
            Metric: "Camera Uptime %",
            Value:
              cameras.length > 0
                ? `${((activeCameras / cameras.length) * 100).toFixed(1)}%`
                : "N/A",
            Status:
              cameras.length > 0 && activeCameras / cameras.length >= 0.95
                ? "Compliant"
                : "Below Target",
          },
          {
            Metric: "Total Incidents",
            Value: incidents.total || 0,
            Status: "Recorded",
          },
          {
            Metric: "Critical/High Incidents",
            Value: criticalIncidents,
            Status:
              criticalIncidents === 0
                ? "No Critical Issues"
                : "Attention Required",
          },
          {
            Metric: "Incident Resolution Rate",
            Value:
              incidents.total > 0
                ? `${((resolvedIncidents / incidents.total) * 100).toFixed(1)}%`
                : "N/A",
            Status:
              incidents.total > 0 &&
              resolvedIncidents / incidents.total >= 0.8
                ? "Compliant"
                : "Below Target",
          },
          {
            Metric: "Total Visitors Recorded",
            Value: visitors.total || 0,
            Status: "Recorded",
          },
          {
            Metric: "Report Period",
            Value: `${dateRange.start} to ${dateRange.end}`,
            Status: "N/A",
          },
          {
            Metric: "Generated On",
            Value: new Date().toLocaleString(),
            Status: "N/A",
          },
        ];

        exportToCSV(
          complianceData,
          null,
          `Regulatory_Compliance_${dateRange.start}_to_${dateRange.end}`
        );
      } else if (reportType === "Security Audit Logs (export)") {
        exportToCSV(
          auditLogs,
          {
            id: "Audit ID",
            type: "Compliance Type",
            description: "Description",
            date: "Date Checked",
            status: "Status",
            standard: "Standard",
          },
          `Security_Audit_Logs_${new Date().toISOString().slice(0, 10)}`
        );
      } else {
        toast.success(`${reportType} prepared`);
        return;
      }

      toast.success(`${reportType} downloaded successfully`);
    } catch (error) {
      console.error(error);
      toast.error("Failed to generate report");
    }
  };

  const getStatusBadge = (status: string) => {
    const colors: Record<string, string> = {
      Compliant: "bg-green-100 text-green-800",
      "Non-Compliant": "bg-red-100 text-red-800",
      Pending: "bg-yellow-100 text-yellow-800",
    };
    return (
      <span
        className={`px-2 py-1 rounded-full text-xs font-semibold ${
          colors[status] || "bg-gray-100 text-gray-800"
        }`}
      >
        {status}
      </span>
    );
  };

  const reportButtons: { title: string; subtitle: string }[] = [
    {
      title: "Daily Security Summary",
      subtitle: "Daily activity snapshot",
    },
    {
      title: "Incident Trend Analysis",
      subtitle: "Incident patterns over the selected period",
    },
    {
      title: "Access Violation Report",
      subtitle: "Access control violations",
    },
    { title: "Visitor Logs", subtitle: "Visitor entry and exit" },
    { title: "CCTV Downtime Report", subtitle: "Camera availability" },
    {
      title: "Guard Deployment Report",
      subtitle: "Shift-wise guard deployment",
    },
    {
      title: "Incident SLA Compliance",
      subtitle: "Incident open/close and SLA notes",
    },
    {
      title: "NABH / ISO / Local Law Summary",
      subtitle: "Regulatory inspection metrics export",
    },
    {
      title: "Security Audit Logs (export)",
      subtitle: "Export current compliance findings below",
    },
  ];

  return (
    <div className="px-2 py-4 w-full h-full mt-10">
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <div className="bg-[#EAFBFA] p-3 rounded-lg">
            <ClipboardList className="w-6 h-6 text-[#0fa9a6]" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Compliance, Audit {"&"} Reporting
            </h1>
            <p className="text-gray-600">
              NABH / ISO / local law compliance, security audit logs, guard
              deployment, incident SLA, and regulatory reports
            </p>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm p-4 mb-4 border border-gray-100">
        <h2 className="text-sm font-semibold text-gray-800 mb-2">Scope</h2>
        <ul className="text-sm text-gray-600 list-disc pl-5 space-y-1">
          <li>
            Compliance: NABH / ISO / local law alignment and inspection-ready
            exports
          </li>
          <li>Security audit logs derived from access, incidents, and visitors</li>
          <li>Shift-wise guard deployment report (CSV)</li>
          <li>Incident SLA compliance export</li>
          <li>
            Reports: daily summary, incident trends, access violations, visitor
            logs, CCTV downtime
          </li>
        </ul>
      </div>

      <div className="bg-white rounded-lg shadow-sm p-4 mb-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="min-w-[150px]">
            <label className="block text-xs font-medium text-gray-500 mb-1">
              Region
            </label>
            <Dropdown
              selectedValue={selectedRegion}
              onChange={setSelectedRegion}
              type="region"
            />
          </div>
          <div className="min-w-[150px]">
            <label className="block text-xs font-medium text-gray-500 mb-1">
              Premises
            </label>
            <Dropdown
              selectedValue={selectedPremises}
              onChange={setSelectedPremises}
              type="premises"
              selectedRegion={selectedRegion}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">
              Report start
            </label>
            <input
              type="date"
              value={dateRange.start}
              onChange={(e) =>
                setDateRange({ ...dateRange, start: e.target.value })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#375998]"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">
              Report end
            </label>
            <input
              type="date"
              value={dateRange.end}
              onChange={(e) =>
                setDateRange({ ...dateRange, end: e.target.value })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#375998]"
            />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow-sm p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Findings</p>
              <p className="text-2xl font-bold text-gray-900">
                {complianceStats.totalAudits}
              </p>
            </div>
            <div className="bg-[#EAFBFA] p-3 rounded-lg">
              <FileText className="w-6 h-6 text-[#0fa9a6]" />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Compliant</p>
              <p className="text-2xl font-bold text-green-600">
                {complianceStats.compliant}
              </p>
            </div>
            <div className="bg-green-100 p-3 rounded-lg">
              <CheckCircle className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Non-compliant</p>
              <p className="text-2xl font-bold text-red-600">
                {complianceStats.nonCompliant}
              </p>
            </div>
            <div className="bg-red-100 p-3 rounded-lg">
              <AlertCircle className="w-6 h-6 text-red-600" />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Pending</p>
              <p className="text-2xl font-bold text-yellow-600">
                {complianceStats.pending}
              </p>
            </div>
            <div className="bg-yellow-100 p-3 rounded-lg">
              <Calendar className="w-6 h-6 text-yellow-600" />
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          Generate reports
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {reportButtons.map((r) => (
            <button
              key={r.title}
              type="button"
              onClick={() => handleGenerateReport(r.title)}
              className="flex items-start gap-3 p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors text-left"
            >
              <Download className="w-5 h-5 text-[#0fa9a6] shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-gray-900">{r.title}</p>
                <p className="text-sm text-gray-500">{r.subtitle}</p>
              </div>
            </button>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          Security audit log (live checks)
        </h2>
        {loading ? (
          <div className="flex justify-center items-center p-8">
            <SpinLoader />
          </div>
        ) : auditLogs.length === 0 ? (
          <div className="flex flex-col items-center justify-center p-12 text-gray-500">
            <CheckCircle className="w-16 h-16 mb-4 text-gray-300" />
            <p className="text-lg font-medium mb-2">No issues in current checks</p>
            <p className="text-sm">Adjust region/premises or refresh data</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b bg-gray-50">
                  <th className="py-3 px-4 font-semibold text-gray-700">Type</th>
                  <th className="py-3 px-4 font-semibold text-gray-700">
                    Description
                  </th>
                  <th className="py-3 px-4 font-semibold text-gray-700">
                    Standard
                  </th>
                  <th className="py-3 px-4 font-semibold text-gray-700">Date</th>
                  <th className="py-3 px-4 font-semibold text-gray-700">Status</th>
                </tr>
              </thead>
              <tbody>
                {auditLogs.map((log) => (
                  <tr key={log.id} className="border-b hover:bg-gray-50">
                    <td className="py-3 px-4 font-medium text-gray-900">
                      {log.type}
                    </td>
                    <td className="py-3 px-4 text-gray-600">{log.description}</td>
                    <td className="py-3 px-4 text-gray-600">{log.standard}</td>
                    <td className="py-3 px-4 text-gray-600">
                      {new Date(log.date).toLocaleDateString()}
                    </td>
                    <td className="py-3 px-4">{getStatusBadge(log.status)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default ComplianceAuditReport;
